import {
  Document,
  PDFViewer,
  Page as PDFPage,
  StyleSheet,
  Text as PDFText,
  View as PDFView,
  Image as PDFImage,
  Font
} from '@react-pdf/renderer';
import { SERVICE_TYPE } from '../../../../../constants';

Font.register({
  family: 'Roboto',
  fonts: [{ src: '/Roboto-Bold.ttf', fontWeight: 'bold' }]
});

const styles = StyleSheet.create({
  page: {
    padding: 20
  },
  row: {
    flexDirection: 'row',
    marginBottom: 0
  },
  boldText: {
    fontFamily: 'Roboto',
    fontWeight: 'ultrabold'
  },
  text: {
    fontSize: 9,
    display: 'inline-block'
  },
  sectionHeading: {
    fontSize: 10,
    fontFamily: 'Roboto',
    marginTop: 10,
    marginBottom: 2
  },
  columnHeader: {
    width: '14%',
    backgroundColor: '#1976D2',
    color: 'white',
    padding: 5,
    textAlign: 'center',
    fontSize: 9
  },
  columnContent: {
    width: '14%',
    padding: 3,
    textAlign: 'center',
    fontSize: 9
  },
  summaryBox: {
    border: '1px Solid black',
    marginTop: 4,
    marginBottom: 4
  },
  summaryRow: {
    flexDirection: 'row',
    borderBottom: '1px Solid black'
  },
  summaryCellLabel: {
    width: '16%',
    padding: 4,
    fontSize: 9,
    fontFamily: 'Roboto',
    borderRight: '1px Solid black'
  },
  summaryCellValue: {
    width: '14%',
    padding: 4,
    fontSize: 9,
    textAlign: 'center',
    borderRight: '1px Solid black'
  }
});

const fullScreenStyles = {
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    zIndex: 1000,
    backgroundColor: 'white'
  }
};
const COLORS = ['#f0f0f0', '#e0e0e0'];

const Page = ({ style, children, ...props }) => (
  <PDFPage style={{ ...styles.page, ...style }} {...props}>
    {children}
  </PDFPage>
);

const View = ({ style, children, ...props }) => (
  <PDFView style={{ ...style }} {...props}>
    {children}
  </PDFView>
);

const Text = ({ style, content, ...props }) => (
  <PDFText style={{ ...styles.text, ...style }} {...props}>
    {content}
  </PDFText>
);

function formatDate(dateString) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).formatToParts(date);
  const get = (type) => parts.find((p) => p.type === type).value;
  return `${get('day')}/${get('month')}/${get('year')}`;
}

const patientName = (patient) =>
  [patient?.name_suffix, patient?.first_name, patient?.last_name].filter(Boolean).join(' ').trim() || '-';

const SectionTable = ({ title, rows, billNoKey, billNoLabel }) => (
  <>
    <Text style={styles.sectionHeading} content={`${title} (${rows.length} records)`} />
    <View style={styles.row}>
      <Text style={{ ...styles.columnHeader, width: '6%' }} content="Sr No." />
      <Text style={{ ...styles.columnHeader, width: '11%' }} content="Date" />
      <Text style={{ ...styles.columnHeader, width: '16%' }} content="Service" />
      <Text style={{ ...styles.columnHeader, width: '23%' }} content="Patient Name" />
      <Text style={{ ...styles.columnHeader, width: '11%' }} content="UHID" />
      <Text style={{ ...styles.columnHeader, width: '11%' }} content={billNoLabel} />
      <Text style={{ ...styles.columnHeader, width: '7%' }} content="Qty" />
      <Text style={{ ...styles.columnHeader, width: '15%' }} content="Item Amt" />
    </View>
    {rows.length === 0 ? (
      <View style={styles.row}>
        <Text style={{ ...styles.columnContent, width: '100%', textAlign: 'left' }} content="No records found for this period." />
      </View>
    ) : (
      rows.map((row, index) => (
        <View key={row._id || index} style={{ ...styles.row, backgroundColor: COLORS[index % COLORS.length] }}>
          <Text style={{ ...styles.columnContent, width: '6%' }} content={index + 1} />
          <Text style={{ ...styles.columnContent, width: '11%' }} content={formatDate(row.date)} />
          <Text style={{ ...styles.columnContent, width: '16%', textAlign: 'left' }} content={row.serviceMaster?.service_name || '-'} />
          <Text style={{ ...styles.columnContent, width: '23%', textAlign: 'left' }} content={patientName(row.patient)} />
          <Text style={{ ...styles.columnContent, width: '11%' }} content={row.patient?.uhid || '-'} />
          <Text style={{ ...styles.columnContent, width: '11%' }} content={row[billNoKey] || '-'} />
          <Text style={{ ...styles.columnContent, width: '7%' }} content={row.service_items?.quantity || 0} />
          <Text style={{ ...styles.columnContent, width: '15%' }} content={Math.round(row.service_items?.net_amt ?? row.service_items?.amount ?? 0)} />
        </View>
      ))
    )}
  </>
);

export const ServiceCollectionReportPdf = ({ data }) => {
  const opdRows = data?.opd?.data || [];
  const ipdRows = data?.ipd?.data || [];
  const emptySummary = {
    bookingCount: 0,
    patientCount: 0,
    billCount: 0,
    bill_amt: 0,
    disc_amt: 0,
    recd_amt: 0,
    bal_amt: 0
  };
  const opdSummary = data?.opd?.summary || emptySummary;
  const ipdSummary = data?.ipd?.summary || emptySummary;
  const totalSummary = data?.total || emptySummary;

  const serviceTypeLabel = data?.service_type
    ? SERVICE_TYPE.find((t) => t._id === data.service_type)?.name || data.service_type
    : null;
  const filterLabel = data?.service?.service_name
    ? `Service : ${data.service.service_name}${serviceTypeLabel ? ` (${serviceTypeLabel})` : ''}`
    : `Service Type : ${serviceTypeLabel || 'All'}`;

  return (
    <div style={fullScreenStyles.container}>
      <PDFViewer width="100%" height="100%" showToolbar>
        <Document title="Service Collection Report">
          <Page style={styles.page} size="A4">
            <PDFImage
              src="/logo_Hospital_m.jpeg"
              style={{ width: '100%', height: '80px', marginBottom: 1, marginRight: '10%' }}
            />
            <Text style={{ ...styles.boldText, textAlign: 'center', fontSize: 11 }} content="SERVICE COLLECTION REPORT" />
            <Text
              style={{ textAlign: 'center', fontSize: 9, marginBottom: 6 }}
              content={`${filterLabel}   |   Period : ${formatDate(data?.from_date)} to ${formatDate(data?.to_date)}`}
            />

            <SectionTable title="OPD SERVICE BOOKINGS" rows={opdRows} billNoKey="op_no" billNoLabel="OP No." />
            <View style={{ marginLeft: '55%', marginTop: '4px', flexDirection: 'row' }}>
              <Text style={{ ...styles.columnContent, width: '35%', textAlign: 'right', color: 'red', fontFamily: 'Roboto' }} content="OPD TOTAL :" />
              <Text
                style={{ ...styles.columnContent, width: '65%', textAlign: 'left' }}
                content={`Bills: ${opdSummary.billCount} | Bill Amt: ${Math.round(opdSummary.bill_amt)} | Disc: ${Math.round(opdSummary.disc_amt)} | Recd: ${Math.round(opdSummary.recd_amt)} | Credit: ${Math.round(opdSummary.bal_amt)}`}
              />
            </View>

            <SectionTable title="IPD SERVICE BOOKINGS" rows={ipdRows} billNoKey="ipd_no" billNoLabel="IPD No." />
            <View style={{ marginLeft: '55%', marginTop: '4px', flexDirection: 'row' }}>
              <Text style={{ ...styles.columnContent, width: '35%', textAlign: 'right', color: 'red', fontFamily: 'Roboto' }} content="IPD TOTAL :" />
              <Text
                style={{ ...styles.columnContent, width: '65%', textAlign: 'left' }}
                content={`Bills: ${ipdSummary.billCount} | Bill Amt: ${Math.round(ipdSummary.bill_amt)} | Disc: ${Math.round(ipdSummary.disc_amt)} | Recd: ${Math.round(ipdSummary.recd_amt)} | Credit: ${Math.round(ipdSummary.bal_amt)}`}
              />
            </View>

            <Text style={styles.sectionHeading} content="GRAND TOTAL (OPD + IPD) — reconciles with Collection Summary" />
            <View style={styles.summaryBox}>
              <View style={styles.summaryRow}>
                <Text style={{ ...styles.summaryCellLabel, fontFamily: 'Roboto' }} content="" />
                <Text style={{ ...styles.summaryCellValue, fontFamily: 'Roboto' }} content="Bills" />
                <Text style={{ ...styles.summaryCellValue, fontFamily: 'Roboto' }} content="Patients" />
                <Text style={{ ...styles.summaryCellValue, fontFamily: 'Roboto' }} content="Bill Amt" />
                <Text style={{ ...styles.summaryCellValue, fontFamily: 'Roboto' }} content="Disc Amt" />
                <Text style={{ ...styles.summaryCellValue, fontFamily: 'Roboto' }} content="Recd Amt" />
                <Text style={{ ...styles.summaryCellValue, fontFamily: 'Roboto', borderRight: 'none' }} content="Credit" />
              </View>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryCellLabel} content="OPD" />
                <Text style={styles.summaryCellValue} content={opdSummary.billCount} />
                <Text style={styles.summaryCellValue} content={opdSummary.patientCount} />
                <Text style={styles.summaryCellValue} content={Math.round(opdSummary.bill_amt)} />
                <Text style={styles.summaryCellValue} content={Math.round(opdSummary.disc_amt)} />
                <Text style={styles.summaryCellValue} content={Math.round(opdSummary.recd_amt)} />
                <Text style={{ ...styles.summaryCellValue, borderRight: 'none' }} content={Math.round(opdSummary.bal_amt)} />
              </View>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryCellLabel} content="IPD" />
                <Text style={styles.summaryCellValue} content={ipdSummary.billCount} />
                <Text style={styles.summaryCellValue} content={ipdSummary.patientCount} />
                <Text style={styles.summaryCellValue} content={Math.round(ipdSummary.bill_amt)} />
                <Text style={styles.summaryCellValue} content={Math.round(ipdSummary.disc_amt)} />
                <Text style={styles.summaryCellValue} content={Math.round(ipdSummary.recd_amt)} />
                <Text style={{ ...styles.summaryCellValue, borderRight: 'none' }} content={Math.round(ipdSummary.bal_amt)} />
              </View>
              <View style={{ ...styles.summaryRow, borderBottom: 'none' }}>
                <Text style={{ ...styles.summaryCellLabel, fontFamily: 'Roboto' }} content="TOTAL" />
                <Text style={{ ...styles.summaryCellValue, fontFamily: 'Roboto' }} content={totalSummary.billCount} />
                <Text style={{ ...styles.summaryCellValue, fontFamily: 'Roboto' }} content={totalSummary.patientCount} />
                <Text style={{ ...styles.summaryCellValue, fontFamily: 'Roboto' }} content={Math.round(totalSummary.bill_amt)} />
                <Text style={{ ...styles.summaryCellValue, fontFamily: 'Roboto' }} content={Math.round(totalSummary.disc_amt)} />
                <Text style={{ ...styles.summaryCellValue, fontFamily: 'Roboto' }} content={Math.round(totalSummary.recd_amt)} />
                <Text style={{ ...styles.summaryCellValue, fontFamily: 'Roboto', borderRight: 'none' }} content={Math.round(totalSummary.bal_amt)} />
              </View>
            </View>
            <Text
              style={{ fontSize: 8, marginTop: 2, color: '#555' }}
              content="Bill Amt = gross bill amount, Disc = discount given, Recd = actually collected (cash+bank, excludes credit), Credit = outstanding/not yet collected."
            />
          </Page>
        </Document>
      </PDFViewer>
    </div>
  );
};

export default ServiceCollectionReportPdf;
